<template>
  <div>
    <div class="chat-banner" role="note">
      <svg class="icon icon-sm" aria-hidden="true"><use href="#i-shield"/></svg>
      <p>{{ $t('intake.portal.chatBanner') }}</p>
    </div>

    <label v-if="!needsLogin && !loading && contacts.length" class="rb-ob-field" for="therapist-chat-contact">
      <span>{{ lang === 'fa' ? 'انتخاب گفت‌وگو از مخاطبان قبلی یا درمانگر تخصیص‌یافته' : 'Choose an existing contact or assigned therapist' }}</span>
      <select id="therapist-chat-contact" :value="peer ? String(peer.id) : ''" :disabled="sending" @change="selectContact($event.target.value)">
        <option value="">{{ lang === 'fa' ? 'انتخاب گفت‌وگو' : 'Choose a conversation' }}</option>
        <option v-for="contact in contacts" :key="contact.id" :value="String(contact.id)">{{ contact.full_name || contact.title || contact.name }}</option>
      </select>
    </label>
    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>
    <div v-else-if="loadFailed" role="alert">
      <p>{{ $t('intake.portal.chatLoadFail') }}</p>
      <button class="btn btn-secondary" type="button" @click="bootstrap">{{ $t('intake.bookingUi.retry') }}</button>
    </div>
    <p v-else-if="!peer" class="rb-ob-status" role="status">{{ $t('intake.portal.chatNoContact') }}</p>

    <div v-else class="chat-thread" :aria-label="$t('intake.portal.chatThreadAria')">
      <p v-if="!bubbles.length" class="rb-ob-status" role="status">{{ $t('intake.portal.chatEmpty') }}</p>
      <article
          v-for="m in bubbles"
          :key="m.id"
          class="chat-bubble"
          :class="m.mine ? 'is-client' : 'is-therapist'"
      >
        <header>
          <b>{{ m.author }}</b>
          <time>{{ m.time }}</time>
        </header>
        <p>{{ m.text }}</p>
        <footer>
          <span class="chat-status">
            {{ m.mine ? $t('intake.portal.chatSent') : $t('intake.portal.chatRead') }}
          </span>
        </footer>
      </article>
    </div>
    <button v-if="peer && !loadFailed" class="btn btn-secondary btn-sm" type="button" :disabled="loading || sending" @click="bootstrap">{{ $t('intake.matchUi.refreshMessages') }}</button>

    <form class="chat-composer" :aria-label="$t('intake.portal.chatComposerAria')" @submit.prevent="send">
      <label class="visually-hidden" for="chat-message">{{ $t('intake.portal.chatInputLabel') }}</label>
      <textarea
          id="chat-message"
          v-model="draft"
          rows="2"
          :disabled="!conversationId || sending"
          :placeholder="$t('intake.portal.chatPlaceholder')"
      ></textarea>
      <div class="chat-composer-bar">
        <small>{{ $t('intake.portal.chatHint') }}</small>
        <button class="btn btn-primary btn-sm" type="submit" :disabled="!conversationId || sending">
          {{ sending ? $t('intake.portal.loading') : $t('intake.portal.chatSend') }}
        </button>
      </div>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
    </form>
  </div>
</template>

<script>
import {
  fetchMessengerContacts,
  fetchMessagesByUser,
  fetchProfile,
  getCachedProfile,
  hasAuthToken,
  normalizeChatMessages,
  resolveChatPeer,
  sendMessengerText,
} from '@/intake/portalApi'

export default {
  name: 'PortalChatStep',
  data() {
    return {
      loading: true,
      loadFailed: false,
      sending: false,
      needsLogin: false,
      peer: null,
      contacts: [],
      conversationId: null,
      bubbles: [],
      draft: '',
      formStatus: '',
      profileId: null,
      loadGeneration: 0,
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
  },
  watch: {
    '$route.fullPath'() { this.draft = ''; this.bootstrap() },
  },
  beforeDestroy() {
    this.loadGeneration++
    this.$emit('chat-peer', null)
  },
  mounted() {
    this.bootstrap()
  },
  methods: {
    preferredUserId() {
      return this.$route.query.userId || this.$route.params.userId || null
    },
    selectContact(userId) {
      if (this.sending) return
      const query = {...this.$route.query}
      if (userId && this.contacts.some(contact => String(contact.id) === String(userId))) query.userId = String(userId)
      else delete query.userId
      return this.$router.replace({name:this.$route.name,params:this.$route.params,query})
    },
    bootstrap() {
      const generation = ++this.loadGeneration
      this.$emit('chat-peer', null)
      this.loading = true
      this.loadFailed = false
      this.peer = null
      this.contacts = []
      this.conversationId = null
      this.bubbles = []
      this.formStatus = ''
      this.needsLogin = !hasAuthToken()
      if (!hasAuthToken()) {
        this.loading = false
        return
      }
      return Promise.all([
        fetchProfile().catch(() => getCachedProfile()),
        fetchMessengerContacts(),
      ])
          .then(([profile, contacts]) => {
            if (generation !== this.loadGeneration) return null
            this.profileId = profile && profile.id
            this.contacts = (Array.isArray(contacts) ? contacts : []).filter(contact => contact && contact.contact_role === 'therapist' && contact.id != null)
            this.peer = resolveChatPeer(this.preferredUserId(), this.contacts, profile)
            if (!this.peer || this.peer.id == null) {
              this.formStatus = this.$t('intake.portal.messengerUnavailable')
              return null
            }
            return fetchMessagesByUser(this.peer.id, 1)
          })
          .then((pack) => {
            if (generation !== this.loadGeneration || !pack) return
            if (!pack.ok) {
              this.loadFailed = true
              return
            }
            this.$emit('chat-peer', this.peer)
            this.conversationId = pack.conversationId
            const peerName = (this.peer && (this.peer.full_name || this.peer.name))
              || this.$t('intake.portal.therapistName')
            this.bubbles = normalizeChatMessages(
                pack.messages,
                this.profileId,
                peerName,
                this.$t('intake.portal.chatYou'),
            )
          })
          .catch(() => {
            if (generation !== this.loadGeneration) return
            this.loadFailed = true
            this.formStatus = this.$t('intake.portal.chatLoadFail')
          })
          .finally(() => {
            if (generation === this.loadGeneration) this.loading = false
          })
    },
    send() {
      const text = (this.draft || '').trim()
      if (!text || !this.conversationId || this.sending) return
      this.sending = true
      this.formStatus = ''
      const generation = this.loadGeneration
      return sendMessengerText(this.conversationId, text)
          .then((saved) => {
            if (generation !== this.loadGeneration) return
            const messages = normalizeChatMessages([saved], this.profileId, '', this.$t('intake.portal.chatYou'))
            if (!messages.length) throw new Error('CHAT_RESPONSE_INVALID')
            this.bubbles.push(messages[0])
            if (this.draft.trim() === text) this.draft = ''
          })
          .catch(() => {
            if (generation !== this.loadGeneration) return
            this.formStatus = this.$t('intake.portal.chatSendFail')
          })
          .finally(() => {
            this.sending = false
          })
    },
  },
}
</script>
